import React from "react";
import {Field, FieldArray, reduxForm} from "redux-form/immutable";
import ActionButton from "../../components/ActionButton";
import FormFieldsTable from "../../components/forms/FormFieldsTable";
import TextInputField from "../../components/forms/TextInputField";
import DropDownInputField from "../../components/forms/DropDownInputField";
import Checkbox from "../../components/forms/Checkbox";
import {fromJS, List, Map} from "immutable";
import FormError from "../../components/forms/FormError";
import BooleanInputField from "../../components/forms/BooleanInputField";
import DateInputField from "../../components/forms/DateInputField";
import styles from "./PromoCodeForm.module.scss";
import Tabs from "../../components/Tabs";
import FieldError from "../../components/forms/FieldError";
import {validateGreaterThan, validateLowerThan, validatePresence} from "../../lib/Validators";
import {getAllShops} from "../../lib/Shops";


// Share validation for both Creation and Edition
export function validateForm(values, props) {
  let errors = Map();
  if (values.get("expires") === "true") {
    // ^ Boolean input field handles strings
    errors = validatePresence(errors, values, "expiryDate");
  }
  errors = validatePresence(errors, values, "code");
  errors = validatePresence(errors, values, "description");
  errors = validateGreaterThan(errors, values, "percentagex100", 0);
  errors = validateLowerThan(errors, values, "percentagex100", 101); // Can be 100% off

  // For items list
  const items = values.get("items");
  if (items) {
    items.forEach((item) => {
      if (!item.get("selected")) {
        return false;
      }

      if (isNaN(parseInt(item.get("limit")))) {
        errors = errors.set("items", "Limit should be an integer");
      } else if (!item.get("id")) {
        errors = errors.set("items", "SKU cannot be empty");
      }
    });
  }
  
  return errors.toJS();
}

export function prepareValuesForSubmission(values) {
  function isTrue(value) {
    return value === true || value === "true";
  }

  const percentagex100 = parseInt(values.get("percentagex100"), 10);
  let updatedValues = values
    .set("percentage", percentagex100 / 100).delete("percentagex100")
    .set("limit", parseInt(values.get("limit"), 10))
    .set("maxItemsPerUsage", parseInt(values.get("maxItemsPerUsage"), 10))
    .set("expires", isTrue(values.get("expires")))
    .set("subscriptionsOnly", isTrue(values.get("subscriptionsOnly")))
    .set("items", values.get("items").reduce((acc, i) => {
      if (isTrue(i.get("selected"))) {
        return acc.push(
          Map().set("type", i.get("type")).set("id", i.get("id")).set("limit", parseInt(i.get("limit")))
        );
      }
      
      return acc;
    }, List([])));
  
  if (updatedValues.get("expires") === false) {
    updatedValues = updatedValues.set("expiryDate", "");
  }
  if (updatedValues.get("restrictionType") === RESTRICTIONS.NONE) {
    updatedValues = updatedValues.set("restrictionId", "");
  }

  return updatedValues;
}


export const TYPES = {
  ONBOARDING: "onboarding",
  CHECKOUT: "checkout",
}
export const RESTRICTIONS = {
  NONE: "none",
  SHOP: "shop",
  CUSTOMER: "customer",
  ACCOUNT: "account",
}
export const STATUSES = {
  ENABLED: "enabled",
  DISABLED: "disabled",
}

const PromoCodeForm = (props) => {
  const {
    errors, handleSubmit, submitting,
  } = props;
  const submitLabel = submitting ? "Standby..." : "Submit";

  // validates :restriction_type,
  //   presence: true,
  //   inclusion: [in: ~w(none shop customer account), if: [type: "checkout"]],
  //   inclusion: [in: ~w(none shop), if: [type: "onboarding"]]

  // def validate_restriction_id(nil, _), do: {:error, "cannot be empty"}
  // def validate_restriction_id("", %{restriction_type: "none"}), do: :ok
  // def validate_restriction_id(val, %{restriction_type: restriction_type})
  //   when is_binary(val) and byte_size(val) > 0 and restriction_type in ~w(shop customer account), do: :ok
  // def validate_restriction_id(_, _), do: {:error, "must be specified when restriction_type is not none"}

  // validates :status,
  //   presence: true,
  //   inclusion: [in: ~w(enabled disabled)]


  return (
    <form onSubmit={handleSubmit}>
      <div className={styles.module}>
        <Tabs>
          <div label="Basic Setup" className="fields">
            <FormFieldsTable>
              <Field
                label="Code"
                name="code"
                placeholder="PARTY01"
                active={!props.edit}
                help={props.edit && "*Codes are not editable"}
                component={TextInputField}
                serverError={props.errors.code}
                />
              <Field
                label="Description"
                name="description"
                placeholder="Description, for internal use only"
                component={TextInputField}
                serverError={props.errors.description}
                />
              <Field
                label="Percentage Discount 0 -> 100"
                name="percentagex100"
                placeholder=""
                component={TextInputField}
                serverError={props.errors.percentage}
                />
              <ExpirationOptions {...props} />
              <tr className="field"><th><b>General Restrictions</b></th></tr>
              <Field
                label="Restrict to Subscription"
                name="subscriptionsOnly"
                component={BooleanInputField}
                serverError={props.errors.subscriptionsOnly}
              />
              <ProductsSelection {...props} />
            </FormFieldsTable>
          </div>
          <div label="Advanced Options" className="fields">
            <FormFieldsTable>
              { <Restriction {...props} /> }
            </FormFieldsTable>
          </div>
        </Tabs>
        <FormError error={errors._error} />
        <div className="buttons">
          <ActionButton onClick={handleSubmit(values => props.onSubmit(values))} label={submitLabel} active={!submitting} />
          <ActionButton label="Cancel" active={!submitting} onClick={props.onCancel} />
          <ActionButton label="Delete" active={!submitting} onClick={props.onDelete} show={props.showDelete} />
        </div>
      </div>
    </form>
  );
}

function Restriction(props) {
  const { restrictionType } = props;

  const codeTypes = fromJS([
    { value: TYPES.ONBOARDING, title: "Onboarding" },
    { value: TYPES.CHECKOUT, title: "Checkout" },
  ]);
  
  const statusOptions = fromJS([
    { value: STATUSES.ENABLED, title: "Enabled" },
    { value: STATUSES.DISABLED, title: "Disabled" },
  ]);

  const batchClientOptions = fromJS([
    { value: "", title: "None" },
    { value: "klaviyo", title: "Klaviyo" },
  ]);
  
  const restrictionTypes = fromJS([
    { value: RESTRICTIONS.NONE, title: "No restriction" },
    { value: RESTRICTIONS.ACCOUNT, title: "Restrict for Account ID" },
    { value: RESTRICTIONS.CUSTOMER, title: "Restrict for Customer ID" },
    { value: RESTRICTIONS.SHOP, title: "Restrict for Shop ID" },
  ]);

  const restrictionIdProps = {
    component: TextInputField,
  }

  if (restrictionType === RESTRICTIONS.SHOP) {
    restrictionIdProps.component = DropDownInputField;
    restrictionIdProps.data = getAllShops();
    restrictionIdProps.valueKey = "id";
    restrictionIdProps.titleKey = "label";
    restrictionIdProps.showBlank = false;
  }

  return (
    <>
      <Field
        label="Restriction Type"
        name="restrictionType"
        component={DropDownInputField}
        data={restrictionTypes}
        showBlank={false}
        serverError={props.errors.restrictionType}
      />
      <Field
        label="Restriction ID"
        name="restrictionId"
        serverError={props.errors.restrictionId}
        {...restrictionIdProps}
      />
      <Field
        label="Usage Limit"
        help="*Use 0 for unlimited"
        name="limit"
        type="number"
        component={TextInputField}
        serverError={props.errors.limit}
      />
      <Field
        label="Max Items per Usage"
        help="*Use 0 for unlimited"
        name="maxItemsPerUsage"
        type="number"
        placeholder=""
        component={TextInputField}
        serverError={props.errors.maxItemsPerUsage}
      />
      <Field
        label="Code Type"
        name="type"
        component={DropDownInputField}
        data={codeTypes}
        showBlank={false}
        serverError={props.errors.type}
        />
      <Field
        label="Tag Rules"
        name="tagRules"
        placeholder="rule1, rule2"
        help="Comma separated list of tags"
        component={TextInputField}
        serverError={props.errors.tagRules}
        />
      {/* TODO Schedule a start date
      <Field
        label="Schedule Start Date"
        name="startDate"
        placeholder=""
        component={DateInputField}
        showDatePicker={props.showDatePicker}
        serverError={props.errors.startDate}
        /> */}
      <Field
        label="Status"
        name="status"
        component={DropDownInputField}
        data={statusOptions}
        showBlank={false}
        serverError={props.errors.status}
        />
      <Field
        label="Batch Client"
        name="batchClient"
        component={DropDownInputField}
        data={batchClientOptions}
        showBlank={false}
        serverError={props.errors.batchClient}
        />
    </>
  );
}

function ProductsSelection(props) {

  return (
    <>
      <tr className="field">
        <th>Restrict to following SKUs</th>
        <td>
          {/* Field only to display error for items */}
          <Field
            name="items"
            component={({input, meta, ...props}) => {
              const error = meta.error || props.serverError;
              if (!error) {
                return null;
              }

              return <FieldError serverError={error} />
            }}
            serverError={props.errors.items}
          />
        </td>
      </tr>
      <FieldArray name="items" component={renderProducts}
        serverError={props.errors.items}
      />
    </>
  );
}

const baseItem = () => {
  return Map().set("type", "sku").set("limit", 0).set("selected", true);
}

const renderProducts = ({ fields }) => (
  <>
    {fields.map((member, index) =>
      <tr key={index} className={styles.itemInputs}>
        <td>
          <div className={styles.checkbox}>
            <Field
              name={`${member}.selected`}
              component={Checkbox}
              table={false}
              />
          </div>
          <div className={styles.limit}>
            <Field
              name={`${member}.limit`}
              component={TextInputField}
              table={false}
            />
          </div>
          <div className={styles.sku}>
            <Field
              name={`${member}.id`}
              component={TextInputField}
              table={false}
            />
          </div>
        </td>
      </tr>
    )}
    <tr className="field">
      <td>
        <ActionButton label="Add SKU"
          onClick={() => fields.push(baseItem())} />
      </td>
    </tr>
  </>
);


function ExpirationOptions(props) {
  const dateField = props.expires && (
    <Field
      label="Expiry Date"
      name="expiryDate"
      placeholder=""
      component={DateInputField}
      showDatePicker={props.showDatePicker}
      serverError={props.errors.expiryDate}
      />
  );
  
  return (
    <>
      <Field
        label="Expires"
        name="expires"
        component={BooleanInputField}
        serverError={props.errors.expires}
        itemProp={{realBoolean: true}}
        />
      { dateField }
    </>
  );
}

PromoCodeForm.defaultProps = {
  stateProvinces: List([]),
  showStateProvince: true,
  showDelete: false,
  showAttentionName: false,
  showPhoneNr: false,
  showEmail: false,
  showDialCodeMismatchWarning: true
}

const ReduxForm = reduxForm({
  form: "PromoCodeForm"
})(PromoCodeForm);

export default ReduxForm;